import React from "react";
import { ArrowRight, Brain, Loader2, Search } from "lucide-react";

const POPULAR = ["BTC", "ETH", "SOL", "BNB", "DOGE", "ARB"];

interface AnalyzeHeroProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onPick: (symbol: string) => void;
  busy?: boolean;
  mobile?: boolean;
}

/** "Analyze Any Token" hero: search box, Analyze button, popular tickers. */
export const AnalyzeHero: React.FC<AnalyzeHeroProps> = ({ value, onChange, onSubmit, onPick, busy, mobile }) => (
  <section className={`glass relative overflow-hidden !rounded-2xl ${mobile ? "p-4" : "px-6 py-8"}`}>
    <img
      src="/analyze-hero.jpg"
      alt=""
      className={`pointer-events-none absolute inset-0 size-full object-cover ${mobile ? "object-[70%_center] opacity-40" : "object-right"}`}
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0d121a]/90 from-40% to-transparent" />
    <div className={`relative flex flex-col ${mobile ? "gap-4" : "max-w-[660px] gap-6"}`}>
      <div className="flex flex-col gap-2">
        <p className={`font-semibold uppercase text-[#00c27e] ${mobile ? "text-xs" : "text-base"}`}>
          AI-Powered Market Intelligence
        </p>
        <h2 className={`font-semibold text-white ${mobile ? "text-2xl leading-8" : "text-5xl leading-[60px] tracking-[-0.96px]"}`}>
          Analyze Any Token
        </h2>
        <p className={`text-[#d4d4d4] ${mobile ? "text-sm leading-5" : "text-base leading-6"}`}>
          Get real-time insights, detect trends and uncover opportunities across all major exchanges.
        </p>
      </div>
      <div className={`flex ${mobile ? "flex-col gap-2" : "items-stretch"}`}>
        <label className={`flex h-12 flex-1 items-center gap-2 border border-white/10 bg-[#0d121a]/80 px-3.5 ${mobile ? "rounded-xl" : "rounded-l-xl"}`}>
          <Search className="size-5 text-[#a3a3a3]" />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSubmit()}
            placeholder="Enter token symbol (e.g., BTC, ETH, SOL)"
            className="min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-[#a3a3a3] focus:outline-none"
          />
        </label>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value.trim() || busy}
          className={`flex h-12 items-center justify-center gap-2 border-2 border-white/[0.12] bg-[#07a66c] px-5 font-semibold text-white transition-colors hover:bg-[#07a66c]/85 disabled:cursor-not-allowed disabled:opacity-60 ${
            mobile ? "rounded-xl" : "rounded-r-xl"
          }`}
        >
          {busy ? <Loader2 className="size-5 animate-spin" /> : <Brain className="size-5" />}
          Analyze
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[#a3a3a3]">Popular:</span>
        {POPULAR.map((symbol) => (
          <button
            key={symbol}
            type="button"
            onClick={() => onPick(symbol)}
            className="rounded-full border border-[#3b4a8a] bg-[#111a3a] px-3 py-0.5 text-sm font-medium text-[#c7d2fe] transition-colors hover:border-[#6366f1]"
          >
            {symbol}
          </button>
        ))}
      </div>
    </div>
  </section>
);

interface TableCardProps {
  icon: React.ReactNode;
  tone: "green" | "red" | "amber";
  title: string;
  subtitle: string;
  /** Right side of the header (search, "View all"). */
  actions?: React.ReactNode;
  /** Column header row. */
  head?: React.ReactNode;
  children: React.ReactNode;
}

const TONE = {
  green: "border-[#00653b] bg-[#07a66c] ",
  red: "border-[#912018] bg-[#d92d20]",
  amber: "border-[#93370d] bg-[#dc6803]",
};

/** Glass card with an icon header and a table body (Analyze page). */
export const TableCard: React.FC<TableCardProps> = ({ icon, tone, title, subtitle, actions, head, children }) => (
  <section className="glass flex min-w-0 flex-col overflow-hidden !rounded-2xl">
    <div className="flex items-center gap-3 p-4">
      <span className="relative flex size-12 shrink-0 items-center justify-center">
        <span className={`absolute inset-0 rounded-full border opacity-40 ${TONE[tone]}`} />
        <span className={`relative flex size-8 items-center justify-center rounded-full text-white [&>svg]:size-5 ${TONE[tone]}`}>
          {icon}
        </span>
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-base font-semibold leading-6 text-white">{title}</span>
        <span className="truncate text-sm leading-5 text-[#a3a3a3]">{subtitle}</span>
      </div>
      {actions}
    </div>
    {head}
    {children}
  </section>
);

export const ViewAllButton: React.FC<{ expanded: boolean; onClick: () => void }> = ({ expanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 px-3.5 py-2 text-sm font-semibold text-[#d4d4d4] transition-colors hover:border-white/25"
  >
    {expanded ? "Show less" : "View all"}
    <ArrowRight className={`size-5 transition-transform ${expanded ? "-rotate-90" : ""}`} />
  </button>
);

/** Small grey header cell row shared by the Analyze tables. */
export const TableHead: React.FC<{ grid: string; cells: Array<{ label: string; align?: "right" }> }> = ({ grid, cells }) => (
  <div className={`${grid} border-y border-white/10 bg-white/[0.02] px-4 py-3 text-xs font-semibold text-[#a3a3a3]`}>
    {cells.map((cell, i) => (
      <span key={i} className={cell.align === "right" ? "text-right" : ""}>
        {cell.label}
      </span>
    ))}
  </div>
);
